import { Component, type ReactNode } from 'react'
import { reportError } from '@/utils/logger'
import styles from './ErrorBoundary.module.css'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Error Boundary component to catch and handle React rendering errors.
 * Prevents the entire app from crashing when a component fails.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    reportError('ErrorBoundary caught an error.', error, errorInfo)
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null })
  }

  handleReload = (): void => {
    window.location.reload()
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className={styles.container}>
          <div className={styles.content}>
            <h1 className={styles.title}>Something went wrong</h1>
            <p className={styles.message}>
              The application encountered an unexpected error. You can try to recover or reload the page.
            </p>
            {this.state.error && (
              <pre className={styles.error}>
                {this.state.error.message}
              </pre>
            )}
            <div className={styles.buttons}>
              <button onClick={this.handleReset} className={styles.button}>
                Try Again
              </button>
              <button onClick={this.handleReload} className={styles.buttonSecondary}>
                Reload Page
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
